/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { KeyRound, MessageSquareText, Smartphone, Coins, TrendingUp, Save, Loader2, Radio, AudioLines, BellRing } from "lucide-react";

const Card = ({ icon: Icon, title, children, testid, badge }) => (
  <div data-testid={testid} className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[#0A1426]"><Icon size={18} className="text-[#2BA3A0]" /><h2 className="font-[Fraunces] text-lg font-bold">{title}</h2></div>
      {badge}
    </div>
    {children}
  </div>
);
const Input = (props) => <input {...props} className="h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] bg-white focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />;

function ProviderForm({ id, title, fields, values, onSaved, icon }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(Object.fromEntries(fields.map(([k]) => [k, values?.[k] || ""]))); }, [values]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try { await api.put(`/integrations/settings/${id}`, { values: form }); toast.success("Clés enregistrées"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Card icon={icon} title={title} testid={`provider-${id}`}
      badge={<span data-testid={`provider-${id}-status`} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${values?.configure ? "bg-[#5CB85C]/15 text-[#2d7a2d]" : "bg-[#D4AF5A]/20 text-[#8A6D2B]"}`}>{values?.configure ? "Configuré" : "Mode simulé"}</span>}>
      <div className="grid sm:grid-cols-2 gap-2">
        {fields.map(([k, label, type]) => (
          <div key={k}>
            <label className="text-[11px] font-bold text-[#0A1426]/60 ml-3">{label}</label>
            {type === "select" ? (
              <select data-testid={`${id}-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] bg-white">
                <option value="">—</option><option value="flutterwave">Flutterwave</option>
              </select>
            ) : (
              <Input data-testid={`${id}-${k}`} type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={label} />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Button data-testid={`${id}-save`} disabled={busy} onClick={save} className="h-11 rounded-full bg-[#0A1426] text-white font-bold"><Save size={16} className="mr-2" /> Enregistrer</Button>
        {values?.configure && (
          <Button data-testid={`${id}-reset`} variant="outline" onClick={async () => { await api.delete(`/integrations/settings/${id}`); toast.success("Clés supprimées — mode simulé"); onSaved(); }}
            className="h-11 rounded-full border-[#D9534F] text-[#D9534F] font-bold">Supprimer les clés</Button>
        )}
      </div>
      <p className="text-[11px] text-[#0A1426]/50 mt-2">Les clés sont stockées côté serveur et masquées à l'affichage. Jamais dans le code.</p>
    </Card>
  );
}

function PlansAdmin() {
  const [plans, setPlans] = useState([]);
  const [fx, setFx] = useState({ EUR: "", CDF: "" });
  const [rev, setRev] = useState(null);
  const load = () => {
    api.get("/billing/plans").then(({ data }) => { setPlans(data.plans); setFx({ EUR: data.fx.EUR, CDF: data.fx.CDF }); });
    api.get("/billing/revenue").then(({ data }) => setRev(data)).catch(() => null);
  };
  useEffect(() => { load(); }, []);
  const savePlan = async (p) => {
    try { await api.patch(`/billing/plans/${p.code}`, { price_usd: Number(p.price_usd), name: p.name, tagline: p.tagline, active: p.active }); toast.success(`${p.name} mis à jour`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveFx = async () => {
    try { await api.put("/billing/fx", { EUR: Number(fx.EUR), CDF: Number(fx.CDF) }); toast.success("Taux mis à jour"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      {rev && (
        <Card icon={TrendingUp} title="Revenus" testid="revenue-card">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[["Encaissé (USD)", `$${rev.total_encaisse_usd}`], ["MRR (USD)", `$${rev.mrr_usd}`], ["Abonnés actifs", rev.abonnes_actifs], ["Paiements", rev.transactions_payees]].map(([l, v]) => (
              <div key={l} className="bg-[#F5F3EE] rounded-2xl p-3"><div className="font-[Fraunces] text-xl font-bold text-[#0A1426]">{v}</div><div className="text-[11px] text-[#0A1426]/60">{l}</div></div>
            ))}
          </div>
        </Card>
      )}
      <Card icon={Coins} title="Grille tarifaire (USD)" testid="plans-admin">
        <div className="flex flex-col gap-2">
          {plans.map((p, i) => (
            <div key={p.code} data-testid={`plan-admin-${p.code}`} className="grid grid-cols-[1fr_90px_auto] sm:grid-cols-[1fr_1fr_90px_auto] gap-2 items-center">
              <Input data-testid={`plan-admin-${p.code}-name`} value={p.name} onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <input value={p.tagline || ""} onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, tagline: e.target.value } : x))} className="hidden sm:block h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" />
              <Input data-testid={`plan-admin-${p.code}-price`} type="number" step="0.5" value={p.price_usd} onChange={(e) => setPlans(plans.map((x, j) => j === i ? { ...x, price_usd: e.target.value } : x))} />
              <Button data-testid={`plan-admin-${p.code}-save`} onClick={() => savePlan(p)} className="h-11 w-11 rounded-full bg-[#F5F3EE] text-[#0A1426] hover:bg-[#2BA3A0] hover:text-white p-0"><Save size={16} /></Button>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[#F5F3EE]">
          <div className="text-[11px] font-bold text-[#0A1426]/60 mb-2">Taux de conversion indicatifs (1 USD =)</div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div><label className="text-[11px] text-[#0A1426]/60 ml-3">EUR</label><Input data-testid="fx-eur" type="number" step="0.01" value={fx.EUR} onChange={(e) => setFx({ ...fx, EUR: e.target.value })} /></div>
            <div><label className="text-[11px] text-[#0A1426]/60 ml-3">CDF</label><Input data-testid="fx-cdf" type="number" step="1" value={fx.CDF} onChange={(e) => setFx({ ...fx, CDF: e.target.value })} /></div>
            <Button data-testid="fx-save" onClick={saveFx} className="h-11 rounded-full bg-[#0A1426] text-white font-bold">OK</Button>
          </div>
        </div>
      </Card>
    </>
  );
}

function SmsAndUssd() {
  const [logs, setLogs] = useState([]);
  const [phone, setPhone] = useState("+243810000002");
  const [text, setText] = useState("");
  const [resp, setResp] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/sms/logs").then(({ data }) => setLogs(data)).catch(() => null);
  useEffect(() => { load(); }, []);
  const dial = async (t) => {
    setBusy(true);
    try { const { data } = await api.post("/ussd/simulate", { phone, text: t }); setResp(data.response); setText(t); load(); }
    catch (e) { toast.error("Erreur USSD"); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card icon={Radio} title="Simulateur USSD (*384*… #)" testid="ussd-sim">
        <div className="flex gap-2 mb-2">
          <Input data-testid="ussd-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+243…" />
          <Button data-testid="ussd-dial" disabled={busy} onClick={() => dial("")} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold px-5">Composer</Button>
        </div>
        <pre data-testid="ussd-screen" className="bg-[#0A1426] text-[#9ff0e6] rounded-2xl p-4 text-[12px] whitespace-pre-wrap min-h-[120px] font-mono">{resp || "Composez le code pour afficher le menu…"}</pre>
        {resp.startsWith("CON") && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {["1", "2", "3", "4"].map((c) => <Button key={c} data-testid={`ussd-choice-${c}`} disabled={busy} onClick={() => dial(text ? `${text}*${c}` : c)} variant="outline" className="h-11 rounded-full border-[#E4E0D5] font-bold">{c}</Button>)}
          </div>
        )}
        <p className="text-[11px] text-[#0A1426]/50 mt-2">Callback réel : <code>POST /api/ussd/callback</code> (format Africa's Talking).</p>
      </Card>
      <Card icon={MessageSquareText} title="Journal SMS" testid="sms-logs" badge={<button data-testid="sms-refresh" onClick={load} className="text-[11px] font-bold text-[#2BA3A0] underline">Actualiser</button>}>
        <div className="max-h-72 overflow-y-auto flex flex-col gap-1.5">
          {logs.length === 0 && <div className="text-[12px] text-[#0A1426]/60">Aucun SMS.</div>}
          {logs.map((l) => (
            <div key={l.id} data-testid={`sms-log-${l.id}`} className="bg-[#F5F3EE] rounded-2xl px-3 py-2">
              <div className="flex justify-between text-[11px] text-[#0A1426]/60"><span>{l.to} · {l.kind}</span><span className={l.statut === "simule" ? "text-[#8A6D2B] font-bold" : "text-[#2d7a2d] font-bold"}>{l.statut}</span></div>
              <div className="text-[13px] text-[#0A1426]">{l.texte}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function AudioCard() {
  const [st, setSt] = useState(null);
  const [tts, setTts] = useState({ texte: "", langue: "fr" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/audio/status").then(({ data }) => setSt(data)).catch(() => null);
  useEffect(() => { load(); }, []);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/audio/generate"); toast.success(`${data.generes} narration(s) générée(s)`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const synth = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/tts", tts); toast.success(`Fichier créé : ${data.fichier}`); new Audio(data.url).play().catch(() => null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!st) return null;
  return (
    <Card icon={AudioLines} title="Narrations audio FR + lingala — voix Mélodie (ElevenLabs)" testid="audio-card"
      badge={<span data-testid="audio-status" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${st.cle_configuree ? "bg-[#5CB85C]/15 text-[#2d7a2d]" : "bg-[#D4AF5A]/20 text-[#8A6D2B]"}`}>{st.cle_configuree ? "Clé OK" : "Clé ElevenLabs absente"}</span>}>
      <p className="text-[13px] text-[#0A1426]/80">{st.generes}/{st.total} fichiers statiques présents dans <code>public/audio/</code>. L'app patient ne contacte jamais ElevenLabs : elle lit ces fichiers (ou la voix du navigateur à défaut).</p>
      {!st.cle_configuree && <p className="text-[12px] text-[#8A6D2B] mt-1">La clé <b>ELEVENLABS_API_KEY</b> est lue depuis le gestionnaire de secrets du backend (jamais dans le code).</p>}
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <Input data-testid="tts-text" value={tts.texte} onChange={(e) => setTts({ ...tts, texte: e.target.value })} placeholder="Texte à synthétiser (test)" />
        <select data-testid="tts-lang" value={tts.langue} onChange={(e) => setTts({ ...tts, langue: e.target.value })} className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] bg-white">
          <option value="fr">Français</option><option value="ln">Lingala</option>
        </select>
        <Button data-testid="tts-synth-btn" disabled={!st.cle_configuree || busy || !tts.texte} onClick={synth} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold disabled:opacity-40">Synthétiser</Button>
      </div>
      <Button data-testid="audio-generate-btn" disabled={!st.cle_configuree || busy} onClick={gen} className="h-11 mt-3 rounded-full bg-[#0A1426] text-white font-bold disabled:opacity-40">
        {busy ? <Loader2 className="animate-spin" size={16} /> : `Générer les ${st.total - st.generes} narrations manquantes`}
      </Button>
    </Card>
  );
}

function RemindersAdmin() {
  const [res, setRes] = useState(null);
  const run = async () => {
    try { const { data } = await api.post("/reminders/run-now"); setRes(data); toast.success(`Rappels envoyés : ${data.sms} SMS · ${data.push} push`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card icon={BellRing} title="Rappels automatiques" testid="reminders-admin">
      <p className="text-[13px] text-[#0A1426]/80">Tâche planifiée plateforme toutes les 15 min (heure de Kinshasa) : chaque rappel part une fois par jour par SMS ou notification push (repli SMS si aucun appareil abonné).</p>
      <Button data-testid="reminders-run-now" onClick={run} className="h-11 mt-3 rounded-full bg-[#2BA3A0] text-white font-bold">Forcer l'envoi des rappels du jour</Button>
      {res && <p data-testid="reminders-run-result" className="text-[12px] text-[#0A1426]/70 mt-2">Résultat : {res.sms} SMS · {res.push} push</p>}
      <p className="text-[13px] text-[#0A1426]/80 mt-4">Rappel aidant à 18 h (Kinshasa) : si le patient n'a pas fait sa séance du jour, ses aidants reçoivent une notification push (repli SMS).</p>
      <Button data-testid="evening-check-run-now" variant="outline" onClick={async () => { try { const { data } = await api.post("/reminders/evening-check/run-now"); toast.success(`Rappels aidant envoyés : ${data.envoyes}`); } catch (e) { toast.error("Erreur"); } }}
        className="h-11 mt-2 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Tester le rappel aidant maintenant</Button>
      <p className="text-[13px] text-[#0A1426]/80 mt-4">Rappel famille hebdo : chaque dimanche à 17 h (Kinshasa), les aidants reçoivent le résumé de la semaine (push, repli SMS) avec le lien vers le Tableau famille à partager.</p>
      <Button data-testid="family-weekly-run-now" variant="outline" onClick={async () => { try { const { data } = await api.post("/reminders/family-weekly/run-now"); toast.success(`Résumés famille envoyés : ${data.envoyes} (${data.semaine})`); } catch (e) { toast.error("Erreur"); } }}
        className="h-11 mt-2 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Tester le rappel famille maintenant</Button>
      <p className="text-[13px] text-[#0A1426]/80 mt-4">Rappel photo repas : à 8h30, 13h30 et 20h (Kinshasa), les patients qui ont l'habitude de photographier ce repas (≥ 3 photos) reçoivent un push s'ils ne l'ont pas encore fait aujourd'hui.</p>
      <Button data-testid="meal-reminders-run-now" variant="outline" onClick={async () => { try { const { data } = await api.post("/reminders/meal/run-now?moment=midi"); toast.success(`Rappels repas envoyés : ${data.envoyes} (${data.moment})`); } catch (e) { toast.error("Erreur"); } }}
        className="h-11 mt-2 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Tester le rappel repas (midi) maintenant</Button>
      <Button data-testid="meal-goal-run-now" variant="outline" onClick={async () => { try { const { data } = await api.post("/reminders/meal-goal/run-now"); toast.success(`Objectifs repas : ${data.felicites} patient(s) félicité(s)`); } catch (e) { toast.error("Erreur"); } }}
        className="h-11 mt-2 ml-2 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Tester les félicitations objectif repas</Button>
    </Card>
  );
}

export default function ProSettings() {
  const [settings, setSettings] = useState(null);
  const load = () => api.get("/integrations/settings").then(({ data }) => setSettings(data)).catch(() => null);
  useEffect(() => { load(); }, []);
  if (!settings) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-settings">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Réglages & intégrations</div>
        <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426]">Configuration</h1>
        <p className="text-[13px] text-[#0A1426]/70 mt-1">Mode SMS actuel : <b data-testid="sms-mode">{settings.sms_mode === "simule" ? "SIMULÉ (journal interne, aucun SMS réel)" : settings.sms_mode}</b></p>
      </div>
      <ProviderForm id="africastalking" title="Africa's Talking (SMS + USSD)" icon={KeyRound} values={settings.africastalking} onSaved={load}
        fields={[["username", "Username"], ["api_key", "API key", "password"], ["sender_id", "Sender ID"], ["ussd_code", "Code USSD (*384*123#)"]]} />
      <ProviderForm id="twilio" title="Twilio (SMS alternatif)" icon={KeyRound} values={settings.twilio} onSaved={load}
        fields={[["account_sid", "Account SID"], ["auth_token", "Auth token", "password"], ["from_number", "Numéro expéditeur"]]} />
      <ProviderForm id="mobile_money" title="Mobile Money (Airtel / M-Pesa / Orange)" icon={Smartphone} values={settings.mobile_money} onSaved={load}
        fields={[["provider", "Fournisseur", "select"], ["public_key", "Clé publique (FLWPUBK…)"], ["secret_key", "Clé secrète (FLWSECK…)", "password"], ["webhook_hash", "Secret hash webhook", "password"]]} />
      <p className="text-[11px] text-[#0A1426]/60 -mt-3 px-2">Webhook Flutterwave à déclarer : <code>{process.env.REACT_APP_BACKEND_URL}/api/flutterwave/webhook</code>. Vérifiez dans votre tableau de bord Flutterwave que les opérateurs RDC (Airtel Money, M-Pesa, Orange Money) sont activés pour votre compte marchand.</p>
      <SmsAndUssd />
      <RemindersAdmin />
      <AudioCard />
      <PlansAdmin />
    </div>
  );
}
